import { Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { PMDialog } from "../../utils/PMDialog";
import { PMButton } from "../../utils/PMButton";
import { ProjectSelector } from "../../utils/moduleSelectors/projectSelector";
import { ModernProjectActivitySelectorField } from "../../utils/moduleSelectors/ModernProjectActivitySelectorField";
import { bulkReassignHours } from "../../../api/hour";
import { toast } from "react-hot-toast";

/**
 * Move several hours to another project + project activity (line).
 */
export const HoursBulkReassignDialog = ({
  open,
  onClose,
  hourIds = [],
  initialProject = null,
  onDone,
}) => {
  const { t } = useTranslation();
  const count = Array.isArray(hourIds) ? hourIds.length : 0;

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title={t("hours.bulk_reassign.title")}
      size="3xl"
    >
      <p className="text-sm text-gray-600 mb-4">
        {t("hours.bulk_reassign.intro", { count })}
      </p>
      <Formik
        enableReinitialize
        initialValues={{
          project_id: initialProject?.id != null ? String(initialProject.id) : null,
          project: initialProject || null,
          project_activity_id: null,
          project_activity: null,
        }}
        validationSchema={Yup.object({
          project_id: Yup.mixed()
            .nullable()
            .test("project-required", t("hours.bulk_reassign.project_required"), (v) => v != null && v !== ""),
          project_activity_id: Yup.string().nullable().required(t("hours.bulk_reassign.activity_required")),
        })}
        onSubmit={async (values, helpers) => {
          helpers.setSubmitting(true);
          try {
            const body = await bulkReassignHours({
              hour_ids: hourIds,
              project_activity_id: parseInt(values.project_activity_id, 10),
            });
            const data = body?.data || body;
            const updated = data?.updated_ids?.length ?? 0;
            const rowErrors = data?.errors || [];
            if (rowErrors.length > 0) {
              toast.error(
                t("hours.bulk_reassign.partial", { updated, failed: rowErrors.length })
              );
            } else {
              toast.success(t("hours.bulk_reassign.success", { count: updated }));
            }
            if (typeof onDone === "function") onDone(data);
            onClose();
          } catch (e) {
            const msg =
              e?.response?.data?.errors?.[0] ||
              e?.response?.data?.message ||
              t("hours.bulk_reassign.error");
            toast.error(typeof msg === "string" ? msg : t("hours.bulk_reassign.error"));
          } finally {
            helpers.setSubmitting(false);
          }
        }}
      >
        {(formik) => {
          const setFieldValueWrapped = (...args) => {
            const [field, value] = args;
            formik.setFieldValue(...args);
            if (field === "project_id") {
              formik.setFieldValue("project_activity_id", null, false);
              formik.setFieldValue("project_activity", null, false);
              if (value == null || value === "") {
                formik.setFieldValue("project", null, false);
              }
            }
          };
          return (
          <Form className="space-y-4">
            <ProjectSelector
              label="hours.bulk_reassign.project_field"
              name="project_id"
              selected={formik.values.project || undefined}
              setFieldValue={setFieldValueWrapped}
              values={formik.values}
              errors={formik.errors}
              zIndex={2700}
              onChange={(e) => {
                if (e?.target?.object) {
                  formik.setFieldValue("project", e.target.object, false);
                }
              }}
            />
            <ModernProjectActivitySelectorField
              label={t("project_activity.label")}
              name="project_activity_id"
              selected={formik.values.project_activity}
              project={formik.values.project}
              setFieldValue={formik.setFieldValue}
              errors={formik.errors}
            />
            <div className="flex justify-end gap-2 pt-2">
              <PMButton type="button" className="bg-gray-200 text-gray-800" onClick={onClose}>
                {t("Avbryt")}
              </PMButton>
              <PMButton
                type="submit"
                className="bg-indigo-600 text-white"
                disabled={formik.isSubmitting || count === 0}
              >
                {formik.isSubmitting ? t("hours.bulk_reassign.saving") : t("hours.bulk_reassign.submit")}
              </PMButton>
            </div>
          </Form>
          );
        }}
      </Formik>
    </PMDialog>
  );
};
